import { SFX } from '../platform/audio';
import { banner } from '../ui/hud';
import { spawnDebris, spawnRing } from './fx';
/* ============================================================
   可破壞部位 —— 「打亮的地方」
   ============================================================

   規格 §5.2 只有兩隻有可破壞部位：WALZE（左前腿＋砲塔核心）與
   ZENIT（兩具砲莢）。兩隻都排在前段（5:00 / 9:00），所以這一課
   是**前九分鐘教的規則**，之後的三隻不再重複。

   ## 為什麼判定是「方位角」不是「準頭」

   支柱四是**火力自動、走位手動** —— 主砲自己瞄準心，玩家從頭到尾
   不控制彈著點。所以「瞄準弱點」這個動作在這個遊戲裡不存在。

   把判定改成**命中點相對機體正面的方位角**之後，玩家要做的事變成
   「繞到那一側去打」，那是走位。WALZE 的砲塔核心在背面、ZENIT 的
   砲莢在左右兩側 —— 三個弱點各自是一句走位指令。

   ## 決定性：不准讀 `matrixWorld`

   弱點理論上該「跟著骨段走」，但骨段的世界座標只有在渲染更新過
   矩陣之後才是對的 —— headless 跑模擬時那些矩陣是舊的甚至沒算過，
   同一顆種子在瀏覽器與 Node 會得到不同結果。所以判定只用
   **固定的方位角扇形 + 機體朝向**，這兩個都是模擬層自己的數字。
   會動的只有畫面上那圈標記（`render/boss-field.ts`），它不參與判定。 */

/** 角度差收斂到 ±π。 */
function wrap(a) {
  while (a > Math.PI) a -= Math.PI * 2;
  while (a < -Math.PI) a += Math.PI * 2;
  return a;
}

/** 生成時初始化結構值。`spawnEnemy` 一定要呼叫 —— 池化物件不清的話
    第二次出場的 WALZE 會頂著上一場打壞的腿出場。 */
export function resetBossParts(e) {
  var T = e.T;
  e.broken = 0; e.legDead = 0; e.podDead = 0;
  if (!T.parts) { e.partHp = null; return; }
  e.partHp = [];
  for (var i = 0; i < T.parts.length; i++) e.partHp.push(T.parts[i].hp * e.hpMax);
  if (e.rig) {
    if (e.rig.legs) for (var l = 0; l < e.rig.legs.length; l++) e.rig.legs[l].dead = 0;
    if (e.rig.pods) for (var p = 0; p < e.rig.pods.length; p++) e.rig.pods[p].dead = 0;
  }
  if (e.marks) for (var m = 0; m < e.marks.length; m++) e.marks[m].visible = true;
}

/** 這一擊打到哪個部位。`hx`/`hz` 是命中點；沒有命中點的傷害來源
    （範圍、吸引、自機爆裂）一律回 −1 = 打在本體上。 */
export function bossPartAt(e, hx, hz) {
  var T = e.T;
  if (!T.parts || !e.partHp || hx === undefined) return -1;
  var b = wrap(Math.atan2(hx - e.pos.x, hz - e.pos.z) - e.face);
  for (var i = 0; i < T.parts.length; i++) {
    if (e.partHp[i] <= 0) continue;
    if (Math.abs(wrap(b - T.parts[i].ang)) < T.parts[i].arc) return i;
  }
  return -1;
}

/** 對某個部位造成傷害。回傳「這一擊要不要給本體加成」。 */
export function damageBossPart(e, i, dmg) {
  if (!e.partHp || e.partHp[i] <= 0) return false;
  e.partHp[i] -= dmg;
  if (e.partHp[i] > 0) return true;
  e.partHp[i] = 0;
  breakPart(e, i);
  return true;
}

/** 部位破壞的實質獎勵。**每一個都要在戰鬥上讀得出來** ——
    只有視覺變化的話，「打亮的地方」會被玩家判定成裝飾。 */
function breakPart(e, i) {
  var T = e.T, p = T.parts[i];
  if (p.id === 'leg') {
    /* 腿廢了就拖著走：移速砍半（`sim/boss-ai.ts` 讀 `legDead`）。 */
    e.legDead = 1;
    if (e.rig && e.rig.legs && e.rig.legs[1]) e.rig.legs[1].dead = 1;
  } else if (p.id === 'core') {
    /* 砲塔核心：`o.broken` 讓造型把核心藏起來，直射砲降一半威力。 */
    e.broken = 1;
  } else if (p.id === 'podR' || p.id === 'podL') {
    /* 每破一具砲莢就少一組彈幕。 */
    e.podDead = (e.podDead || 0) + 1;
    var k = p.id === 'podR' ? 1 : 3;
    if (e.rig && e.rig.pods && e.rig.pods[k]) e.rig.pods[k].dead = 1;
  }
  if (e.marks && e.marks[i]) e.marks[i].visible = false;
  spawnRing(e.pos.x, e.pos.z, 0xffd24a, e.r * 0.5, e.r * 4.5, 0.55);
  spawnDebris(e.pos.x, e.T.y + 1.2, e.pos.z, e.T.body, 14, 1.5, e.r * 0.28);
  SFX.boom();
  banner('部位破壞 · ' + p.n);
}

/** 還有幾個弱點沒破（HUD 用）。 */
export function bossPartsLeft(e) {
  if (!e.partHp) return 0;
  var n = 0;
  for (var i = 0; i < e.partHp.length; i++) if (e.partHp[i] > 0) n++;
  return n;
}
